import api from './api';

/**
 * 報表相關API服務
 */
export const reportService = {
    /**
     * 獲取每日報表
     * @param {Object} params - 查詢參數
     * @param {string} params.fleet_id - 車隊ID
     * @param {string} params.start_date - 開始日期
     * @param {string} params.end_date - 結束日期
     * @returns {Promise<Object>} 每日報表數據
     */
    getDailyReport: async (params = {}) => {
        const response = await api.get('/reports/daily', { params });
        return response.data;
    },

    /**
     * 獲取司機績效報表
     * @param {Object} params - 查詢參數
     * @param {string} params.fleet_id - 車隊ID
     * @param {string} params.driver_id - 司機ID
     * @param {string} params.start_date - 開始日期
     * @param {string} params.end_date - 結束日期
     * @returns {Promise<Object>} 司機績效報表數據
     */
    getDriverPerformance: async (params = {}) => {
        const response = await api.get('/reports/drivers', { params });
        return response.data;
    },

    /**
     * 獲取單個司機績效報表
     * @param {string|number} driverId - 司機ID
     * @param {Object} params - 查詢參數
     * @param {string} params.start_date - 開始日期
     * @param {string} params.end_date - 結束日期
     * @returns {Promise<Object>} 司機績效報表數據
     */
    getDriverPerformanceById: async (driverId, params = {}) => {
        const response = await api.get(`/reports/drivers/${driverId}`, { params });
        return response.data;
    },

    /**
     * 獲取收入報表
     * @param {Object} params - 查詢參數
     * @param {string} params.fleet_id - 車隊ID
     * @param {string} params.period - 時間週期 (daily, weekly, monthly, yearly)
     * @param {string} params.start_date - 開始日期
     * @param {string} params.end_date - 結束日期
     * @returns {Promise<Object>} 收入報表數據
     */
    getRevenueReport: async (params = {}) => {
        const response = await api.get('/reports/revenue', { params });
        return response.data;
    },

    /**
     * 獲取訂單趨勢
     * @param {Object} params - 查詢參數
     * @param {string} params.fleet_id - 車隊ID
     * @param {string} params.period - 時間週期 (daily, weekly, monthly)
     * @returns {Promise<Array>} 訂單趨勢數據
     */
    getOrderTrends: async (params = {}) => {
        const response = await api.get('/reports/orders/trends', { params });
        return response.data;
    },

    /**
     * 獲取車隊比較報表
     * @param {Object} params - 查詢參數
     * @param {Array} params.fleet_ids - 車隊ID陣列
     * @param {string} params.metric - 比較指標
     * @param {string} params.start_date - 開始日期
     * @param {string} params.end_date - 結束日期
     * @returns {Promise<Object>} 車隊比較報表數據
     */
    getFleetComparison: async (params = {}) => {
        const response = await api.get('/reports/fleets/comparison', { params });
        return response.data;
    },

    /**
     * 匯出報表
     * @param {string} reportType - 報表類型 (daily, driver, revenue)
     * @param {Object} params - 查詢參數
     * @param {string} params.fleet_id - 車隊ID
     * @param {string} params.start_date - 開始日期
     * @param {string} params.end_date - 結束日期
     * @param {string} params.format - 匯出格式 (csv, excel, pdf)
     * @returns {Promise<Blob>} 報表檔案數據
     */
    exportReport: async (reportType, params = {}) => {
        const response = await api.get(`/reports/${reportType}/export`, {
            params,
            responseType: 'blob'
        });

        // 建立檔名
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'report';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch.length === 2) {
                filename = filenameMatch[1];
            }
        }

        // 建立下載連結
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        return response.data;
    },

    /**
     * 匯出司機績效報表
     * @param {Object} params - 查詢參數
     * @param {string} params.format - 匯出格式 (csv, excel, pdf)
     * @returns {Promise<Blob>} 報表檔案數據
     */
    exportDriverReport: async (params = {}) => {
        return reportService.exportReport('drivers', params);
    },

    /**
     * 匯出每日報表
     * @param {Object} params - 查詢參數
     * @param {string} params.format - 匯出格式 (csv, excel, pdf)
     * @returns {Promise<Blob>} 報表檔案數據
     */
    exportDailyReport: async (params = {}) => {
        return reportService.exportReport('daily', params);
    },

    /**
     * 匯出收入報表
     * @param {Object} params - 查詢參數
     * @param {string} params.format - 匯出格式 (csv, excel, pdf)
     * @returns {Promise<Blob>} 報表檔案數據
     */
    exportRevenueReport: async (params = {}) => {
        return reportService.exportReport('revenue', params);
    },

    // 匯出車隊比較報表
    async exportFleetComparisonReport(params = {}) {
        try {
            const response = await api.get('/reports/fleets/export', {
                params,
                responseType: 'blob'
            });

            // 觸發文件下載
            const filename = `fleet_comparison_${new Date().toISOString().split('T')[0]}.xlsx`;
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();

            return response.data;
        } catch (error) {
            console.error('匯出車隊比較報表失敗:', error);
            throw error;
        }
    }
};

export default reportService;